import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Calendar } from "@/components/ui/calendar";
import { it } from "date-fns/locale";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";
import { Check } from "lucide-react";

const SLOTS = ["09:00", "10:00", "11:00", "14:00", "15:00", "16:00", "17:00", "18:00"];

export const Prenota = () => {
  const { data: types = [] } = useQuery({ queryKey: ["session-types"], queryFn: () => api.get("/session-types").then((r) => r.data) });
  const [form, setForm] = useState({ name: "", email: "", phone: "", session_type: "primo_colloquio", notes: "" });
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [busy, setBusy] = useState(false);

  const selected = types.find((t) => t.key === form.session_type);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !time) { toast.error("Scegli data e orario"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        date: date.toLocaleDateString("it-IT"),
        time,
        origin_url: window.location.origin,
      };
      const res = await api.post("/bookings", payload);
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.success("Richiesta inviata! Ti contatteremo per confermare il tuo primo colloquio gratuito.");
        setForm({ name: "", email: "", phone: "", session_type: "primo_colloquio", notes: "" });
        setDate(null); setTime("");
      }
    } catch {
      toast.error("Prenotazione non riuscita. Riprova.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pt-32 pb-24 relative z-10">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <Reveal>
          <p className="eyebrow mb-4">Prenota</p>
          <h1 className="font-display text-4xl sm:text-6xl text-white leading-tight max-w-3xl">Prenota la tua sessione.</h1>
          <p className="text-zinc-400 mt-6 max-w-xl">Scegli la tipologia, la data e l'orario. Il primo colloquio è sempre gratuito e senza impegno.</p>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-2 gap-8">
          <Reveal>
            <form onSubmit={submit} data-testid="booking-form" className="bg-[#121212] border border-white/10 rounded-2xl p-8 space-y-5">
              <div>
                <label className="text-sm text-zinc-400 mb-2 block">Tipo di sessione</label>
                <Select value={form.session_type} onValueChange={(v) => setForm({ ...form, session_type: v })}>
                  <SelectTrigger data-testid="booking-session-type" className="bg-[#0A0A0A] border-[#27272A]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {types.map((t) => (
                      <SelectItem key={t.key} value={t.key}>{t.label} {t.price > 0 ? `— €${t.price}` : "— gratis"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-zinc-400 mb-2 block">Nome</label>
                  <input value={form.name} onChange={upd("name")} required data-testid="booking-name" className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-md px-4 py-3 outline-none focus:border-[#C19A5B]" />
                </div>
                <div>
                  <label className="text-sm text-zinc-400 mb-2 block">Telefono</label>
                  <input value={form.phone} onChange={upd("phone")} data-testid="booking-phone" className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-md px-4 py-3 outline-none focus:border-[#C19A5B]" />
                </div>
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-2 block">Email</label>
                <input type="email" value={form.email} onChange={upd("email")} required data-testid="booking-email" className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-md px-4 py-3 outline-none focus:border-[#C19A5B]" />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-2 block">Orario</label>
                <Select value={time} onValueChange={setTime}>
                  <SelectTrigger data-testid="booking-time" className="bg-[#0A0A0A] border-[#27272A]">
                    <SelectValue placeholder="Scegli un orario" />
                  </SelectTrigger>
                  <SelectContent>
                    {SLOTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-2 block">Note (facoltative)</label>
                <textarea value={form.notes} onChange={upd("notes")} rows={3} data-testid="booking-notes" className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-md px-4 py-3 outline-none focus:border-[#C19A5B] resize-none" />
              </div>
              <button type="submit" disabled={busy} data-testid="booking-submit" className="w-full rounded-full bg-[#C19A5B] text-black font-semibold px-6 py-3.5 hover:bg-[#D4AF37] transition-colors disabled:opacity-60">
                {busy ? "Attendi…" : selected && selected.price > 0 ? `Prenota e paga €${selected.price}` : "Prenota (gratis)"}
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="bg-[#121212] border border-white/10 rounded-2xl p-8">
              <label className="text-sm text-zinc-400 mb-4 block">Scegli la data</label>
              <div className="flex justify-center">
                <Calendar mode="single" locale={it} selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} data-testid="booking-calendar" className="rounded-md" />
              </div>
              {selected && (
                <div className="mt-8 border-t border-white/10 pt-6 space-y-3">
                  <div className="flex justify-between text-zinc-300"><span>Sessione</span><span className="text-white">{selected.label}</span></div>
                  <div className="flex justify-between text-zinc-300"><span>Data</span><span className="text-white">{date ? date.toLocaleDateString("it-IT") : "—"} {time}</span></div>
                  <div className="flex justify-between text-lg"><span className="text-zinc-300">Totale</span><span className="gold-text font-semibold">{selected.price > 0 ? `€${selected.price}` : "Gratis"}</span></div>
                  {selected.price > 0 && <p className="text-xs text-zinc-500 flex items-center gap-2"><Check size={13} className="text-[#C19A5B]" /> Pagamento sicuro con Stripe · carta di test 4242 4242 4242 4242</p>}
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
};

export default Prenota;
